import { get, post, put, del } from "../../../services/axiosUitils";

// API Response Types (Based on actual API responses)
export interface ApiSuccessResponse {
  code: string;
  message: string;
}

export interface AddMedicationResponse {
  success: ApiSuccessResponse;
  message: string;
  timing_id: string;
  medication_id: string;
  time: string;
  frequency: string;
  status: "not_taken" | "taken";
}

export interface MarkDoneResponse {
  success: ApiSuccessResponse;
  message: string;
  medication_name: string;
  time: string;
  status: "taken";
}

// Request Types
export interface AddMedicationRequest {
  medication_name: string;
  time: string; // Format: "HH:MM"
  frequency: string;
}

export interface MarkDoneRequest {
  timing_id: string;
}

// Home Page Medication Reminder Type (from HomeService)
export interface MedicationReminder {
  "Timing ID": string;
  "Medication ID": string;
  "Medication Name": string;
  "Medication Time": string;
  "Full Time String": string;
  Status: string;
  Frequency: string;
}

// Local Storage Medication Type
export interface LocalMedication {
  timing_id: string;
  medication_id: string;
  medication_name: string;
  time: string;
  frequency: string;
  status: "not_taken" | "taken";
  created_at?: string;
}

class MedicationService {
  private static instance: MedicationService;

  private constructor() { }

  public static getInstance(): MedicationService {
    if (!MedicationService.instance) {
      MedicationService.instance = new MedicationService();
    }
    return MedicationService.instance;
  }

  /**
   * Add new medication
   */
  async addMedication(
    medicationData: AddMedicationRequest
  ): Promise<AddMedicationResponse> {
    const response = await post<AddMedicationResponse>(
      "/food_scanner/medication/crud/",
      medicationData
    );

    // Check if API returned success
    if (response?.success?.code === "Created") {
      return response;
    } else {
      throw new Error(response?.message ?? "Failed to add medication");
    }
  }

  /**
   * Mark medication as taken/done
   */
  async markMedicationDone(timing_id: string): Promise<MarkDoneResponse> {
    try {
      const response = await post<MarkDoneResponse>(
        "/food_scanner/medication/done/",
        { timing_id }
      );

      // Check if API returned success
      if (
        response.success?.code === "Retrieved" &&
        response.status === "taken"
      ) {
        return response;
      } else {
        throw new Error(
          response.message || "Failed to mark medication as done"
        );
      }
    } catch (error: any) {
      // Re-throw with detailed error message
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw error;
    }
  }

  /**
   * Get all medications (if API exists)
   * Note: This endpoint may not exist yet
   */
  async getAllMedications(): Promise<LocalMedication[]> {
    try {
      console.log("🌐 Calling GET /food_scanner/medication/crud/");
      const response = await get<
        LocalMedication[] | { medications: LocalMedication[] } | any
      >("/food_scanner/medication/crud/");

      console.log("📡 Raw GET response:", response);

      // Handle different response formats

      // Format 1: Direct array of medications
      if (Array.isArray(response)) {
        console.log("✅ Response is array, length:", response.length);
        return response;
      }

      // Format 2: Object with 'medications' key
      if (
        response &&
        typeof response === "object" &&
        "medications" in response &&
        Array.isArray(response.medications)
      ) {
        console.log("✅ Response has medications key, length:", response.medications.length);
        return response.medications;
      }

      // Format 3: Single medication object (convert to array)
      if (
        response &&
        typeof response === "object" &&
        "timing_id" in response &&
        "medication_name" in response
      ) {
        console.log("✅ Response is single medication object");
        return [response as LocalMedication];
      }

      // Format 4: Object with results key (pagination)
      if (
        response &&
        typeof response === "object" &&
        "results" in response &&
        Array.isArray(response.results)
      ) {
        console.log("✅ Response has results key, length:", response.results.length);
        return response.results;
      }

      console.log("⚠️ Unknown response format, returning empty array");
      return [];
    } catch (error: any) {
      console.error("❌ getAllMedications error:", error);

      // Handle specific error codes
      if (error.response?.status === 404) {
        console.warn(
          "⚠️ Medication API endpoint not found (404). Using fallback."
        );
        return [];
      }
      if (error.response?.status === 500) {
        console.error(
          "❌ Medication API server error (500):",
          error.response?.data
        );
        return [];
      }
      // Return empty array for any other errors
      console.error("❌ Failed to fetch medications:", error);
      return [];
    }
  }

  /**
   * Update medication (if API exists)
   */
  async updateMedication(
    timing_id: string,
    medicationData: Partial<AddMedicationRequest>
  ): Promise<AddMedicationResponse> {
    try {
      // API expects timing_id in the body, not in URL
      const payload = {
        timing_id,
        ...medicationData,
        status: "not_taken", // Default status
      };

      const response = await put<AddMedicationResponse>(
        `/food_scanner/medication/crud/`,
        payload
      );
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error("Medication API not available");
      }
      if (error.response?.status === 500) {
        throw new Error("Server error while updating medication");
      }
      throw new Error(
        error.response?.data?.message ?? "Failed to update medication"
      );
    }
  }

  /**
   * Delete medication (if API exists)
   */
  async deleteMedication(
    timing_id: string
  ): Promise<{ success: ApiSuccessResponse; message: string }> {
    try {
      // API expects timing_id in the body, not in URL
      const response = await del<{
        success: ApiSuccessResponse;
        message: string;
      }>(`/food_scanner/medication/crud/`, {
        data: { timing_id }, // DELETE request with body
      });
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error("Medication API not available");
      }
      if (error.response?.status === 500) {
        throw new Error("Server error while deleting medication");
      }
      throw new Error(
        error.response?.data?.message ?? "Failed to delete medication"
      );
    }
  }

  /**
   * Convert AddMedicationResponse to LocalMedication format
   */
  convertToLocalMedication(
    response: AddMedicationResponse,
    medicationName: string
  ): LocalMedication {
    return {
      timing_id: response.timing_id,
      medication_id: response.medication_id,
      medication_name: medicationName,
      time: response.time,
      frequency: response.frequency,
      status: response.status,
      created_at: new Date().toISOString(),
    };
  }

  /**
   * Validate medication time format
   */
  validateTime(time: string): boolean {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  /**
   * Format time to display format
   */
  formatTimeForDisplay(time: string): string {
    if (!this.validateTime(time)) {
      return time;
    }

    const [hours, minutes] = time.split(":");
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? "PM" : "AM";

    return `${hour12}:${minutes} ${ampm}`;
  }

  /**
   * Get medication frequency options
   */
  getFrequencyOptions(): string[] {
    return [
      "Once daily",
      "Twice daily",
      "Three times daily",
      "Before breakfast",
      "After breakfast",
      "Before lunch",
      "After lunch",
      "Before dinner",
      "After dinner",
      "Before bedtime",
      "As needed",
    ];
  }
}

// Export singleton instance
const medicationService = MedicationService.getInstance();
export default medicationService;
